import type { Project } from '@p2b/contract';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, NavLink, Navigate, Route, Routes, useLocation, useParams } from 'react-router';
import { ErrorNote } from '../App.js';
import { api, projectRef } from '../api.js';
import { formatDate, formatDay, useI18n } from '../i18n.js';
import { Icon } from '../ui.js';
import { AccessTab } from './project/Access.js';
import { AuditTab } from './project/Audit.js';
import { DeliveriesTab, DeliverDialog } from './project/Deliveries.js';
import { FilesTab } from './project/Files.js';
import { EditProjectDialog } from './project/ProjectDialogs.js';
import { headStill } from './project/model.js';
import { DeliverContext, ModelStill, useDeliveries } from './project/shared.js';

export function ProjectPage() {
  const { t } = useI18n();
  const { org, slug } = useParams();
  const ref = projectRef(org!, slug!);
  const project = useQuery({ queryKey: ['project', ref], queryFn: () => api.project(ref) });
  const loc = useLocation();
  // On a narrow screen the tabs scroll sideways: keep the current one in view.
  useEffect(() => {
    document.querySelector('.tabs a.active')?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [loc.pathname, project.data]);
  if (project.isPending) return <div className="center muted">{t('loading')}</div>;
  if (project.error) return <ErrorNote error={project.error} />;
  return <ProjectView p={project.data} />;
}

function ProjectView({ p }: { p: Project }) {
  const { t } = useI18n();
  const { org, slug } = useParams();
  const caps = new Set(p.capabilities ?? []);
  // Deliveries first: they are what a project is for, and the tab a project opens on. Clients see only them.
  const tabs = [
    caps.has('read:published') || caps.has('read:all')
      ? { path: 'deliveries', label: t('deliveries') }
      : null,
    caps.has('read:all') || caps.has('read:inbox') ? { path: 'files', label: t('files') } : null,
    caps.has('read:all') ? { path: 'access', label: t('access') } : null,
    caps.has('read:audit') ? { path: 'audit', label: t('audit') } : null,
  ].filter((x): x is { path: string; label: string } => x !== null);
  const base = `/projects/${org}/${slug}`;
  const [deliverFor, setDeliverFor] = useState<string | null>(null);

  return (
    <DeliverContext.Provider value={(rev) => setDeliverFor(rev ?? '')}>
      <div className="page project-page">
        <ProjectHead p={p} />
        {tabs.length > 1 && (
          <nav className="tabs" aria-label={p.name}>
            {tabs.map((tab) => (
              <NavLink key={tab.path} to={`${base}/${tab.path}`}>
                {tab.label}
              </NavLink>
            ))}
          </nav>
        )}
        <Routes>
          <Route index element={<Navigate to={`${base}/${tabs[0]?.path ?? 'deliveries'}`} replace />} />
          <Route path="deliveries" element={<DeliveriesTab project={p} />} />
          {/* Earlier addresses of the two tabs that became Deliveries. */}
          <Route path="publications" element={<Navigate to={`${base}/deliveries`} replace />} />
          <Route path="revisions" element={<Navigate to={`${base}/deliveries`} replace />} />
          <Route path="files/*" element={<FilesTab project={p} />} />
          <Route path="access" element={<AccessTab project={p} />} />
          <Route path="audit" element={<AuditTab project={p} />} />
        </Routes>
        {deliverFor !== null && (
          <DeliverDialog project={p} initial={deliverFor || undefined} onClose={() => setDeliverFor(null)} />
        )}
      </div>
    </DeliverContext.Provider>
  );
}

/** Name, place and status, the model still, the facts in a title-block strip, and the two main actions. */
function ProjectHead({ p }: { p: Project }) {
  const { t, lang } = useI18n();
  const { revs, staff, manage, active, latest } = useDeliveries(p);
  const [editing, setEditing] = useState(false);
  const place = [p.description, p.address && p.address !== p.name ? p.address : null, p.municipality].filter(
    Boolean,
  );
  const viewerUrl = latest?.viewerUrl ?? null;
  const still = headStill(revs.data, latest);
  const newest = revs.data?.[0];
  const undelivered = newest && !active.some((x) => x.revisionId === newest.id) ? newest : null;
  // The same rule as the organization page: delivered once a delivery is in force.
  const delivered = active.length > 0;
  const deliver = (
    <DeliverContext.Consumer>
      {(open) =>
        manage && (revs.data?.length ?? 0) > 0 ? (
          <button onClick={() => open(undelivered?.id)}>
            <Icon.Send />
            {t('deliver')}
          </button>
        ) : null
      }
    </DeliverContext.Consumer>
  );

  return (
    <header className="project-head2">
      {viewerUrl ? (
        <a
          className="project-thumb"
          href={viewerUrl}
          target="_blank"
          rel="noreferrer"
          title={t('openViewer')}
        >
          <ModelStill src={still} />
        </a>
      ) : (
        <span className="project-thumb">
          <ModelStill src={still} />
        </span>
      )}
      <div className="project-ident">
        <Link className="crumb" to={`/orgs/${p.org}`}>
          {p.orgName}
        </Link>
        <div className="project-title-row">
          <h1 className="project-title">{p.name}</h1>
          <span className={`tag dot${delivered ? ' ok' : ''}`}>
            {t(delivered ? 'delivered' : 'inProgress')}
          </span>
        </div>
        {(place.length > 0 || p.landNumber) && (
          <div className="project-meta">
            {[place.join(', '), p.landNumber ? `${t('landNumber')} ${p.landNumber}` : null]
              .filter(Boolean)
              .join(' · ')}
          </div>
        )}
      </div>
      <div className="project-actions">
        {manage && (
          <button className="quiet" onClick={() => setEditing(true)}>
            {t('edit')}
          </button>
        )}
        {deliver}
        {viewerUrl && (
          <a className="button primary" href={viewerUrl} target="_blank" rel="noreferrer">
            <Icon.Model />
            {t('openViewer')}
          </a>
        )}
      </div>
      <dl className="project-strip">
        <div>
          <dt>{t('client')}</dt>
          <dd>{p.orgName}</dd>
        </div>
        <div>
          <dt>{t('lastDelivered')}</dt>
          <dd>{latest ? `${latest.revisionLabel}, ${formatDay(latest.publishedAt, lang)}` : '—'}</dd>
        </div>
        {staff && undelivered && (
          <div>
            <dt>{t('frozenNotDelivered')}</dt>
            <dd>{undelivered.label}</dd>
          </div>
        )}
        {!staff && (
          <div>
            <dt>{t('deliveries')}</dt>
            <dd>{active.length}</dd>
          </div>
        )}
        {p.lastActivityAt && (
          <div>
            <dt>{t('lastActivity')}</dt>
            <dd>{formatDate(p.lastActivityAt, lang)}</dd>
          </div>
        )}
      </dl>
      {editing && <EditProjectDialog project={p} onClose={() => setEditing(false)} />}
    </header>
  );
}
